import { notificationClient } from '../config/api';
import {
  Notification,
  NotificationListResponse,
  NotificationStatus,
} from '../shared/types/Notification';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class NotificationService {
  async getNotifications(page = 1, limit = 10): Promise<NotificationListResponse> {
    try {
      const skip = (page - 1) * limit;
      const response = await notificationClient.get<{
        data: Array<{
          id: string;
          userId: string;
          title: string;
          message: string;
          type: string;
          isRead: boolean;
          createdAt: string;
          priority?: string;
          category?: string;
          data?: string;
        }>;
        pagination: { skip: number; take: number; count: number };
      }>('/api/notifications', {
        params: { skip, take: limit },
      });

      if (!response.data || !response.data.data) {
        throw new Error('Erro ao buscar notificações');
      }

      const notifications: Notification[] = response.data.data.map((n) => ({
        id: n.id,
        userId: n.userId,
        title: n.title,
        message: n.message,
        type: this.mapNotificationType(n.type),
        status: n.isRead ? NotificationStatus.READ : NotificationStatus.UNREAD,
        // Garantir que a data seja interpretada como UTC se não tiver timezone
        createdAt: this.parseUtcDate(n.createdAt),
        priority: n.priority,
        category: n.category,
        data: n.data ? JSON.parse(n.data) : undefined,
      }));

      const unreadCount = notifications.filter(
        (n) => n.status === NotificationStatus.UNREAD
      ).length;

      return {
        notifications,
        unreadCount,
        total: response.data.pagination.count,
      };
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao buscar notificações');
    }
  }

  private mapNotificationType(type: string): any {
    return type;
  }

  /**
   * Converte string de data para Date, garantindo que seja interpretada como UTC
   * Backend salva em UTC mas pode serializar sem o sufixo 'Z'
   */
  private parseUtcDate(dateString: string): Date {
    if (!dateString) {
      return new Date();
    }

    // Se a string já termina com Z ou tem timezone (+/-), usar diretamente
    if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
      return new Date(dateString);
    }

    // Caso contrário, adicionar 'Z' para indicar que é UTC
    return new Date(dateString + 'Z');
  }

  async markAsRead(notificationId: string): Promise<void> {
    try {
      await notificationClient.put(`/api/notifications/${notificationId}/read`);
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao marcar notificação como lida');
    }
  }

  async markAllAsRead(): Promise<void> {
    try {
      await notificationClient.put('/api/notifications/read-all');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao marcar todas como lidas');
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    try {
      await notificationClient.delete(`/api/notifications/${notificationId}`);
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao deletar notificação');
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      const response = await notificationClient.get<{ unreadCount: number }>(
        '/api/notifications/unread-count'
      );

      return response.data?.unreadCount || 0;
    } catch {
      return 0;
    }
  }
}

export const notificationService = new NotificationService();
